import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { OnboardingStatus } from '../../lib/api/modules/onboarding';
import { useAuth } from '../../contexts/AuthContext';

/** Roles that have an onboarding (medopd-docs 102); everyone else never asks for it. */
export const ONBOARDING_ROLES = ['clinic_admin', 'doctor'] as const;

/** Query key: per active clinic, so switching clinic shows that clinic's setup. */
export function onboardingKey() {
  let clinic: string | null = null;
  try {
    clinic = sessionStorage.getItem('active_clinic_id');
  } catch {
    clinic = null;
  }
  return ['onboarding', clinic ?? 'home'] as const;
}

/** The signed-in person's onboarding at their active clinic, or undefined for roles without one. */
export function useOnboardingStatus() {
  const { userData } = useAuth();
  const enabled = !!userData && (ONBOARDING_ROLES as readonly string[]).includes(userData.role);
  const query = useQuery({
    queryKey: onboardingKey(),
    enabled,
    staleTime: 30_000,
    retry: 1,
    queryFn: async () => {
      const res = await api.onboarding.status();
      if (res.error) throw new Error(res.error);
      return res.data as OnboardingStatus;
    },
  });
  return { ...query, enabled };
}

/** Skip / un-skip / confirm / finish; each answers with the new status, which replaces the cached one. */
export function useOnboardingActions() {
  const qc = useQueryClient();
  const apply = (s: OnboardingStatus | undefined) => {
    if (s) qc.setQueryData(onboardingKey(), s);
  };
  const run = (call: () => Promise<{ data?: OnboardingStatus; error?: string }>) => async () => {
    const res = await call();
    if (res.error) throw new Error(res.error);
    return res.data;
  };
  const skip = useMutation({ mutationFn: (key: string) => run(() => api.onboarding.skip(key))(), onSuccess: apply });
  const unskip = useMutation({ mutationFn: (key: string) => run(() => api.onboarding.unskip(key))(), onSuccess: apply });
  const confirm = useMutation({ mutationFn: (key: string) => run(() => api.onboarding.confirm(key))(), onSuccess: apply });
  const complete = useMutation({ mutationFn: () => run(() => api.onboarding.complete())(), onSuccess: apply });
  /** Re-read the status after a step's own form saved. */
  const refresh = () => qc.invalidateQueries({ queryKey: ['onboarding'] });
  return { skip, unskip, confirm, complete, refresh };
}
